import { createRxDatabase, addRxPlugin } from 'rxdb';
import type { RxDatabase, RxCollection } from 'rxdb';
import { getRxStorageDexie } from 'rxdb/plugins/storage-dexie';
import { wrappedValidateAjvStorage } from 'rxdb/plugins/validate-ajv';
import { RxDBDevModePlugin } from 'rxdb/plugins/dev-mode';
import { RxDBMigrationPlugin } from 'rxdb/plugins/migration-schema';
import { pendingPulls } from './sync/syncState';

addRxPlugin(RxDBMigrationPlugin);

import {
  transactionSchema,
  exchangeRateSchema,
  categorySchema,
  paymentMethodSchema,
  branchSchema,
  appConfigSchema,
  userProfileSchema,
  serviceSchema,
  customerSchema,
  staffSchema,
  packageSchema,
  customerPackageSchema,
  serviceCategorySchema,
  syncQueueSchema,
} from './schemas';
import type {
  TransactionDocType,
  ExchangeRateDocType,
  CategoryDocType,
  PaymentMethodDocType,
  BranchDocType,
  AppConfigDocType,
  UserProfileDocType,
  ServiceDocType,
  CustomerDocType,
  StaffDocType,
  PackageDocType,
  CustomerPackageDocType,
  ServiceCategoryDocType,
  SyncQueueDocType,
} from './schemas';
import { lwwConflictHandler } from './sync/conflict-handler';

// Add dev mode plugin in development
if (import.meta.env.DEV) {
  addRxPlugin(RxDBDevModePlugin);
}

// Type definitions for the database collections
export type DepilawayCollections = {
  transactions: RxCollection<TransactionDocType>;
  exchange_rates: RxCollection<ExchangeRateDocType>;
  categories: RxCollection<CategoryDocType>;
  payment_methods: RxCollection<PaymentMethodDocType>;
  branches: RxCollection<BranchDocType>;
  app_config: RxCollection<AppConfigDocType>;
  user_profiles: RxCollection<UserProfileDocType>;
  services: RxCollection<ServiceDocType>;
  customers: RxCollection<CustomerDocType>;
  staff: RxCollection<StaffDocType>;
  packages: RxCollection<PackageDocType>;
  customer_packages: RxCollection<CustomerPackageDocType>;
  service_categories: RxCollection<ServiceCategoryDocType>;
  sync_queue: RxCollection<SyncQueueDocType>;
};

export type DepilawayDatabase = RxDatabase<DepilawayCollections>;

let dbPromise: Promise<DepilawayDatabase> | null = null;
let dbInstance: DepilawayDatabase | null = null;
let currentDbUserId: string | null = null;

export async function cleanupOrphanDatabases(currentUserId: string) {
  if (!window.indexedDB || !window.indexedDB.databases) return;
  try {
    const dbs = await window.indexedDB.databases();
    const SEVEN_DAYS = 7 * 24 * 60 * 60 * 1000;
    const now = Date.now();

    const userLastUsed = new Map<string, number>();
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('db_last_used_')) {
        const userId = key.replace('db_last_used_', '');
        const time = parseInt(localStorage.getItem(key) || '0', 10);
        userLastUsed.set(userId, time);
      }
    }

    for (const dbInfo of dbs) {
      const dbName = dbInfo.name;
      if (!dbName) continue;
      
      if (dbName.startsWith('rxdb-db-depilaway_') || dbName.startsWith('rxdb-dexie-depilaway_')) {
        if (dbName.includes(`depilaway_${currentUserId}`)) {
          continue;
        }

        let matchedUserId: string | null = null;
        for (const userId of userLastUsed.keys()) {
           if (dbName.includes(`depilaway_${userId}`)) {
             matchedUserId = userId;
             break;
           }
        }

        let shouldDelete = false;
        if (matchedUserId) {
            const lastUsed = userLastUsed.get(matchedUserId) || 0;
            if (now - lastUsed > SEVEN_DAYS) {
                shouldDelete = true;
            }
        } else {
            shouldDelete = true;
        }

        if (shouldDelete) {
            console.log(`[Sync V2] Cleaning up orphan database: ${dbName}`);
            window.indexedDB.deleteDatabase(dbName);
        }
      }
    }
  } catch (e) {
    console.error('[Sync V2] Error during orphan database cleanup:', e);
  }
}

export function initDatabase(userId: string): Promise<DepilawayDatabase> {
  if (dbPromise && currentDbUserId === userId) return dbPromise;

  if (dbPromise && currentDbUserId !== userId) {
    console.log(`[Sync V2] User changed from ${currentDbUserId} to ${userId}. Closing previous database instance.`);
    if (dbInstance) {
      const oldDb = dbInstance;
      oldDb.close().catch(err => console.error('[Sync V2] Error closing old db instance:', err));
      dbInstance = null;
    }
    dbPromise = null;
  }

  currentDbUserId = userId;

  dbPromise = (async () => {
    let storage: any = getRxStorageDexie();
    
    cleanupOrphanDatabases(userId).catch(console.error);
    localStorage.setItem('db_last_used_' + userId, Date.now().toString());
    
    if (import.meta.env.DEV) {
      const { wrappedValidateAjvStorage } = await import('rxdb/plugins/validate-ajv');
      storage = wrappedValidateAjvStorage({ storage });
    }

    const db = await createRxDatabase<DepilawayCollections>({
      name: `depilaway_${userId}`,
      storage,
      multiInstance: true,
      eventReduce: true,
      ignoreDuplicate: import.meta.env.DEV,
    });

    
    

    await db.addCollections({
      transactions: {
        schema: transactionSchema,
        conflictHandler: lwwConflictHandler,
        
      },
      exchange_rates: {
        schema: exchangeRateSchema,
        conflictHandler: lwwConflictHandler,
        
      },
      categories: {
        schema: categorySchema,
        conflictHandler: lwwConflictHandler,
        
      },
      payment_methods: {
        schema: paymentMethodSchema,
        conflictHandler: lwwConflictHandler,
        
      },
      branches: {
        schema: branchSchema,
        conflictHandler: lwwConflictHandler,
        
      },
      app_config: {
        schema: appConfigSchema,
        conflictHandler: lwwConflictHandler,
        
      },
      user_profiles: {
        schema: userProfileSchema,
        conflictHandler: lwwConflictHandler,
        
      },
      services: {
        schema: serviceSchema,
        conflictHandler: lwwConflictHandler,
        
      },
      customers: {
        schema: customerSchema,
        conflictHandler: lwwConflictHandler,
        
      },
      staff: {
        schema: staffSchema,
        conflictHandler: lwwConflictHandler,
        
      },
      packages: {
        schema: packageSchema,
        conflictHandler: lwwConflictHandler,
        
      },
      customer_packages: {
        schema: customerPackageSchema,
        conflictHandler: lwwConflictHandler,
        
      },
      service_categories: {
        schema: serviceCategorySchema,
        conflictHandler: lwwConflictHandler,
        
      },
      sync_queue: {
        schema: syncQueueSchema,
      },
    });

    console.log('[Database] addCollections finished.');
    console.log('[Database] db.branches is:', db.branches);
    console.log('[Database] db.app_config is:', db.app_config);
    console.log('[Database] collections keys:', Object.keys(db.collections));

    const collectionsToWatch = [
      'transactions', 'exchange_rates', 'categories', 'payment_methods', 'branches',
      'app_config', 'user_profiles', 'services', 'customers', 'staff', 'packages',
      'customer_packages', 'service_categories'
    ];

    collectionsToWatch.forEach((collectionName) => {
      const col = db[collectionName as keyof DepilawayCollections];
      if (col) {
        col.postInsert(
          function (this: any, docData: any, doc: any) {
            if (pendingPulls.has(docData.id)) return;
            db.sync_queue.upsert({
              id: docData.id,
              collection_name: collectionName,
              action: 'push',
              updated_at: new Date().toISOString()
            }).catch((err: any) => console.error('[Sync V2] Error in postInsert hook:', err));
          },
          false
        );

        col.postSave(
          function (this: any, docData: any, doc: any) {
            if (pendingPulls.has(docData.id)) return;
            db.sync_queue.upsert({
              id: docData.id,
              collection_name: collectionName,
              action: 'push',
              updated_at: new Date().toISOString()
            }).catch((err: any) => console.error('[Sync V2] Error in postSave hook:', err));
          },
          false
        );

        col.postRemove(
          function (this: any, docData: any, doc: any) {
            if (pendingPulls.has(docData.id)) return;
            db.sync_queue.upsert({
              id: docData.id,
              collection_name: collectionName,
              action: 'delete',
              updated_at: new Date().toISOString()
            }).catch((err: any) => console.error('[Sync V2] Error in postRemove hook:', err));
          },
          false
        );
      }
    });



    dbInstance = db;
    return db;
  })();

  return dbPromise;
}

export function getDatabase(): DepilawayDatabase | null {
  return dbInstance;
}

export function getDbPromise(): Promise<DepilawayDatabase> | null {
  return dbPromise;
}
